import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize, switchMap } from 'rxjs';
import { ReservationService } from '../../../services/reservation.service';
import { RestaurantService } from '../../../services/restaurant.service';
import { CLOSED_STATUSES } from '../../../models/reservation';
import { toServiceDate } from '../../../services/reservation-time.util';
import { PickerOnlyDateDirective } from '../../../directives/picker-only-date.directive';
import {
  DEFAULT_BOOKING_RANGE,
  DateException,
  DateExceptionKind,
  HoursRange,
  MAX_EXCEPTION_DAYS_AHEAD,
  ServingHoursDoc,
  bookingDayError,
  dateLabel,
  to12h,
} from '../../../utils/serving-hours.util';

/**
 * Closed dates, and in Reservation settings special booking hours (FNF-1181),
 * saved one at a time. Restaurant Hours and Reservation settings both use this
 * (FNF-1612), so a date added on one page shows on the other and the two
 * can't drift. A closed date closes the whole restaurant: no orders, bookings
 * or walk-ins.
 */
@Component({
  selector: 'app-date-exceptions',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, PickerOnlyDateDirective],
  templateUrl: './date-exceptions.component.html',
  styleUrl: './date-exceptions.component.css',
})
export class DateExceptionsComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly reservationService = inject(ReservationService);
  private readonly restaurantService = inject(RestaurantService);
  private readonly toastr = inject(ToastrService);

  /** Every exception in the hours document, past ones included. */
  @Input() set exceptions(value: DateException[] | null | undefined) {
    this.all = [...(value ?? [])];
  }
  /** List and edit special (booking-window) hours too; otherwise closed dates only. */
  @Input() showSpecialHours = false;
  /** Offer "Special hours" when adding a date (timed reservation modes). */
  @Input() offerSpecialHours = false;
  /** The row's title; leave empty where the page already gives one. */
  @Input() heading = '';
  /** The hours document after each change. */
  @Output() changed = new EventEmitter<ServingHoursDoc>();

  private all: DateException[] = [];
  exceptionForm: FormGroup = this.newExceptionForm();
  /** The date being edited, or null when adding a new one. */
  editingExceptionDate: string | null = null;
  savingException = false;
  /** Bookings already on the date being closed; null when not checked. */
  bookingsOnDate: number | null = null;
  /** Today on the restaurant's clock, the earliest date that can be chosen. */
  today = toServiceDate(new Date());
  maxExceptionDate = this.shiftDate(this.today, MAX_EXCEPTION_DAYS_AHEAD);

  ngOnInit(): void {
    this.reservationService.timeZone().subscribe((tz) => {
      this.today = toServiceDate(new Date(), tz);
      this.maxExceptionDate = this.shiftDate(this.today, MAX_EXCEPTION_DAYS_AHEAD);
    });
  }

  /** What this page lists: upcoming dates, in order. */
  get upcoming(): DateException[] {
    return this.all
      .filter((e) => e.date >= this.today && (this.showSpecialHours || e.kind === 'closed'))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  get exceptionRanges(): FormArray {
    return this.exceptionForm.get('ranges') as FormArray;
  }

  get exceptionKind(): DateExceptionKind {
    return this.exceptionForm.get('kind')!.value;
  }

  /**
   * Closing a date here replaces special booking hours this page doesn't
   * list (they are set in Reservation settings).
   */
  get replacesSpecialHours(): boolean {
    const date = this.exceptionForm.get('date')!.value;
    return (
      !this.showSpecialHours &&
      !!date &&
      date !== this.editingExceptionDate &&
      this.all.some((e) => e.date === date && e.kind === 'hours')
    );
  }

  exceptionDateLabel(serviceDate: string): string {
    return dateLabel(serviceDate, true);
  }

  exceptionSummary(e: DateException): string {
    return e.kind === 'closed'
      ? 'Closed'
      : e.hours.map((h) => `${to12h(h.start_time)} – ${to12h(h.end_time)}`).join(', ');
  }

  /** Why the exception can't be saved yet, or null. */
  get exceptionError(): string | null {
    const v = this.exceptionForm.getRawValue() as {
      date: string;
      kind: DateExceptionKind;
      ranges: HoursRange[];
      note: string;
    };
    if (!v.date) {
      return 'Pick a date.';
    }
    if (v.date < this.today) {
      return 'Pick today or a later date.';
    }
    if (v.date > this.maxExceptionDate) {
      return 'That date is too far ahead.';
    }
    if ((v.note ?? '').length > 60) {
      return 'Keep the note to 60 characters.';
    }
    return v.kind === 'hours' ? bookingDayError(v.ranges) : null;
  }

  addExceptionRange(): void {
    const last = this.exceptionRanges.at(this.exceptionRanges.length - 1)?.value as
      HoursRange | undefined;
    this.exceptionRanges.push(
      this.rangeGroup(last ? { start_time: last.end_time, end_time: '' } : DEFAULT_BOOKING_RANGE),
    );
  }

  removeExceptionRange(index: number): void {
    if (this.exceptionRanges.length > 1) {
      this.exceptionRanges.removeAt(index);
    }
  }

  editException(e: DateException): void {
    this.exceptionForm = this.newExceptionForm();
    this.exceptionRanges.clear();
    (e.hours.length ? e.hours : [DEFAULT_BOOKING_RANGE]).forEach((r) =>
      this.exceptionRanges.push(this.rangeGroup(r)),
    );
    this.exceptionForm.patchValue({ date: e.date, kind: e.kind, note: e.note ?? '' });
    this.editingExceptionDate = e.date;
  }

  cancelException(): void {
    this.exceptionForm = this.newExceptionForm();
    this.editingExceptionDate = null;
    this.bookingsOnDate = null;
  }

  saveException(): void {
    if (this.exceptionError || this.savingException) {
      this.exceptionForm.markAllAsTouched();
      return;
    }
    const v = this.exceptionForm.getRawValue() as {
      date: string;
      kind: DateExceptionKind;
      ranges: HoursRange[];
      note: string;
    };
    const ranges = [...v.ranges].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const replacing = this.editingExceptionDate;
    this.savingException = true;
    const save = () =>
      this.restaurantService.setDateException(v.date, {
        kind: v.kind,
        hours: v.kind === 'hours' ? ranges : [],
        note: (v.note ?? '').trim() || null,
      });
    // Moving an exception to another date removes the old one first.
    const run =
      replacing && replacing !== v.date
        ? this.restaurantService.removeDateException(replacing).pipe(switchMap(save))
        : save();
    run.pipe(finalize(() => (this.savingException = false))).subscribe({
      next: (doc) => {
        this.afterExceptionsChanged(doc);
        const label = dateLabel(v.date);
        this.toastr.success(
          v.kind === 'closed'
            ? `Closed all day on ${label}: no orders, bookings or walk-ins.`
            : `Bookings on ${label} follow these hours only.`,
          v.kind === 'closed' ? 'Date closed' : 'Special hours saved',
        );
        this.cancelException();
      },
      error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
    });
  }

  removeException(e: DateException): void {
    this.savingException = true;
    this.restaurantService
      .removeDateException(e.date)
      .pipe(finalize(() => (this.savingException = false)))
      .subscribe({
        next: (doc) => {
          this.afterExceptionsChanged(doc);
          this.toastr.success(
            `${dateLabel(e.date)} follows the weekly hours again.`,
            e.kind === 'closed' ? 'Date reopened' : 'Special hours removed',
          );
          if (this.editingExceptionDate === e.date) {
            this.cancelException();
          }
        },
        error: (err) => this.toastr.error(this.errorMessage(err), 'Error'),
      });
  }

  /** A row of a FormArray, typed for [formGroup] in the template. */
  asGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  private afterExceptionsChanged(doc: ServingHoursDoc): void {
    this.all = [...(doc.date_exceptions ?? [])];
    this.changed.emit(doc);
    // Republish, so the book drops its cached hours.
    const current = this.restaurantService.currentRestaurantDetails$.value;
    if (current) {
      this.restaurantService.currentRestaurantDetails$.next({ ...current, serving_hours: doc });
    }
    // Closing today changes whether the restaurant is open now.
    this.restaurantService.refreshHoursStatus().subscribe();
  }

  private newExceptionForm(): FormGroup {
    const form = this.fb.group({
      date: ['', Validators.required],
      kind: ['closed' as DateExceptionKind],
      ranges: this.fb.array([this.rangeGroup(DEFAULT_BOOKING_RANGE)]),
      note: ['', Validators.maxLength(60)],
    });
    form.get('date')!.valueChanges.subscribe(() => this.checkBookingsOnDate());
    form.get('kind')!.valueChanges.subscribe(() => this.checkBookingsOnDate());
    return form;
  }

  private rangeGroup(r: HoursRange): FormGroup {
    return this.fb.group({
      start_time: [r.start_time, Validators.required],
      end_time: [r.end_time, Validators.required],
    });
  }

  /**
   * Closing a date leaves its bookings in place (reservation-service does not
   * cancel them), so say how many there are before the owner saves.
   */
  private checkBookingsOnDate(): void {
    const { date, kind } = this.exceptionForm.getRawValue() as {
      date: string;
      kind: DateExceptionKind;
    };
    this.bookingsOnDate = null;
    if (!date || kind !== 'closed' || date < this.today) {
      return;
    }
    this.reservationService.listByDate(date).subscribe({
      next: (rows) => {
        if (this.exceptionForm.get('date')!.value === date) {
          this.bookingsOnDate = rows.filter(
            (r) => r.kind === 'reservation' && !CLOSED_STATUSES.includes(r.status),
          ).length;
        }
      },
      error: () => (this.bookingsOnDate = null),
    });
  }

  private shiftDate(serviceDate: string, days: number): string {
    const [y, m, d] = serviceDate.split('-').map(Number);
    const next = new Date(y, m - 1, d + days);
    return [
      next.getFullYear(),
      String(next.getMonth() + 1).padStart(2, '0'),
      String(next.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private errorMessage(err: any): string {
    if (err instanceof Error) {
      return err.message;
    }
    const detail = err?.error?.detail ?? err?.error?.message;
    return typeof detail === 'string' ? detail : 'Unable to save the date.';
  }
}
